import { useCallback, useEffect, useState } from "react";
import { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { toast } from "react-toastify";
import { errorTitle, isResponseErrorArray, modifyNullParams, modifyToValues } from "@monorepo/helpers";
import { useCalculateAmountMutation } from "@monorepo/services/api/services";
import { ICalculateAmountPayload, IServiceParameter } from "@monorepo/types";

type TUseCalculateAmountProps<T extends FieldValues> = {
  serviceId?: string;
  methods: UseFormReturn<T>;
  counters?: Array<IServiceParameter>;
  isThirdPartyPE?: boolean;
  id?: string;
};

export function useCalculateAmount<T extends FieldValues>({
  serviceId,
  methods,
  counters,
  isThirdPartyPE,
  id, //used for resetting amount to zero
}: TUseCalculateAmountProps<T>) {
  const [calculate] = useCalculateAmountMutation();
  const [amount, setAmount] = useState<number | undefined>(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (id) {
      setAmount(0);
    }
  }, [id]);

  const handleCalculate = useCallback(async () => {
    if (!serviceId) {
      return;
    }
    let values = methods.getValues() as ICalculateAmountPayload["params"];
    if (isThirdPartyPE && counters) {
      values = modifyToValues({ values, counters });
    }
    if (values && counters?.length) {
      const paramsKeys = [
        ...Object.keys(values).filter((x: any) => [...counters.map((y) => y.parameter_name)].includes(x)),
        ...Object.keys(values).filter((x) => /(AMT|CUSNO)+/.test(x)),
      ];

      let params: Record<string, string> = {};
      paramsKeys.forEach((x) => {
        params[x] = values[x];
      });
      setIsLoading(true);
      calculate({
        service_id: serviceId,
        params: modifyNullParams(params),
      })
        .unwrap()
        .then((res) => {
          methods.clearErrors("calculateError" as Path<T>);
          setAmount(res.meta.data.AMT);
        })
        .catch((err) => {
          toast.error(err?.message);

          if (isResponseErrorArray(err)) {
            toast.error(err[0]?.title);

            // toast.error(err?.message);
            methods.setError("calculateError" as Path<T>, { type: "counterError", message: errorTitle(err) });
          }
        })
        .finally(() => setIsLoading(false));
    }
  }, [calculate, counters, methods, serviceId, isThirdPartyPE]);

  return { amount, handleCalculate, isLoading };
}
